import React, { Fragment, useRef, useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/auth';
import { Dialog, Transition } from '@headlessui/react';
import { DocumentPlusIcon, ClockIcon, TrashIcon, BriefcaseIcon, EyeSlashIcon, MapPinIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';
import { getJustificationsCompanyList } from '../services/api_justifications';
import { listOrganizationalUnits } from "../services/api_org_unit";
import ComboboxAutocomplete from './combobox_autocomplete';
import { createEmployeeJustificationRequest, deleteJustificationRequest } from '../services/api_justification_requests';
import Swal from 'sweetalert2';
import moment from 'moment';
import { getDayCalendarWorkingHour } from '../services/api_calendar';
import LocalBarIcon from "@mui/icons-material/LocalBar";
import CustomTooltipComponent from './custom_tooltip_component';
import { estraiMessaggiErrore } from '../services/utils';
import ModalDayHeader from './modal_day_header';
import CircularProgress from '@mui/material/CircularProgress';

const formatItalianDate = (date) => {
    if (!date) return '';

    const parsedDate = moment(date, 'YYYY-MM-DD', true);
    if (!parsedDate.isValid()) return '';

    const formatted = new Intl.DateTimeFormat('it-IT', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
    }).format(new Date(`${parsedDate.format('YYYY-MM-DD')}T00:00:00`));

    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};


const AddGiustificativoModalAssignWH = ({
    closeModalAddGiustificativi,
    dipendente,
    formattedDay,
    selectedUnit,
    onAfterSave,

    onSwitchToWorkingArea,
    canManageJustifications = false,
    canManageWorkingAreas = false,
    canViewWorkingAreas = false,
}) => {
    const cancelButtonRef = useRef(null);
    const { token, profile } = useContext(AuthContext);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingJR, setIsLoadingJR] = useState(false);
    const [isError, setIsError] = useState(false);
    const [isErrorJR, setIsErrorJR] = useState(false);
    const [error, setError] = useState('');
    const [isLoadingOu, setIsLoadingOU] = useState(false);
    const [giustificativi, setGiustificativi] = useState([]);
    const [giustificativoSelected, setGiustificativoSelected] = useState(null);
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [startDay, setStartDay] = useState(formatDate(formattedDay));
    const [endDay, setEndDay] = useState(formatDate(formattedDay));
    const [note, setNote] = useState('');
    const [timeError, setTimeError] = useState('');
    const [isInputReset, setIsInputReset] = useState(false);
    const [units, setUnits] = useState([]);
    const [tempSelectedUnit, setTempSelectedUnit] = useState(null);
    const [employeeDayDetails, setEmployeeDayDetails] = useState(null);
    const [isForbidden, setIsForbidden] = useState(false);
    const [currentDay, setCurrentDay] = useState(formattedDay || null);
    const controllerRef = useRef(null);
    const modalInitialFocusRef = useRef(null);

    const isSenzaCalendario = dipendente?.shift_type === 'senza_calendario';

    useEffect(() => {
        setCurrentDay(formattedDay || null);
    }, [formattedDay]);

    const hasManageJustificationPermission =
        profile.employee?.permissions?.includes('hr.manage_justification_ou') ||
        profile.employee?.permissions?.includes('hr.manage_justification_all');

    const hasViewTimbraturePermission =
        profile.employee?.permissions?.includes('hr.view_inouts_ou') ||
        profile.employee?.permissions?.includes('hr.view_inouts_all') ||
        profile.employee?.permissions?.includes('hr.manage_inouts_ou') ||
        profile.employee?.permissions?.includes('hr.manage_inouts_all');

    const hasViewShiftsPermission =
        profile.employee?.permissions?.includes('hr.view_shifts_ou') ||
        profile.employee?.permissions?.includes('hr.view_shifts_all') ||
        profile.employee?.permissions?.includes('hr.manage_shifts_ou') ||
        profile.employee?.permissions?.includes('hr.manage_shifts_all');

    const isEmployee = profile.customer_type === 'employee';
    
    useEffect(() => {
        if (selectedUnit) {
            setTempSelectedUnit(selectedUnit);
        }
    }, [selectedUnit]);

    function formatDate(dateString) {
        const date = moment(dateString, 'YYYY-MM-DD', true);
        return date.isValid() ? date.format('DD/MM/YYYY') : '';
    }
    
    const weekStart = formattedDay
        ? moment(formattedDay).startOf("isoWeek").format("YYYY-MM-DD")
        : null;
    const weekEnd = formattedDay
        ? moment(formattedDay).endOf("isoWeek").format("YYYY-MM-DD")
        : null;

    const canGoPrevious = Boolean(
        currentDay &&
        weekStart &&
        moment(currentDay).isAfter(weekStart, "day")
    );
    const canGoNext = Boolean(
        currentDay &&
        weekEnd &&
        moment(currentDay).isBefore(weekEnd, "day")
    );

    const resetJustificationForm = () => {
        setGiustificativoSelected(null);
        setStartTime("");
        setEndTime("");
        setNote("");
        setTimeError("");
    };

    const handlePreviousDay = () => {
        resetJustificationForm();

        setCurrentDay((current) =>
            moment(current)
                .subtract(1, "day")
                .format("YYYY-MM-DD")
        );
    };

    const handleNextDay = () => {
        resetJustificationForm();

        setCurrentDay((current) =>
            moment(current)
                .add(1, "day")
                .format("YYYY-MM-DD")
        );
    };

    useEffect(() => {
        setStartDay(formatDate(currentDay));
        setEndDay(formatDate(currentDay));
    }, [currentDay]);

    const fetchEmployeeDayDetails = async () => {
        if (dipendente?.id && currentDay && token) {
            const employeeId = dipendente.id;
            
            if (controllerRef.current) {
                controllerRef.current.abort();
            }

            const controller = new AbortController();
            controllerRef.current = controller;
            const signal = controller.signal;

            try {
                setIsLoading(true);
                setIsError(false);
                setIsForbidden(false);

                const calendarResponse = await getDayCalendarWorkingHour(
                    token,
                    currentDay,
                    null,
                    [employeeId],
                    1, 
                    10,
                    signal
                );

                const modifiedCalendarResponse = calendarResponse.results.map((entry) => ({
                    ...entry,
                    days: entry.days.map((day) => ({
                        ...day,
                        justification_requests:
                            isEmployee && !hasManageJustificationPermission
                                ? []
                                : day.justification_requests,
                        inouts: isEmployee && !hasViewTimbraturePermission ? [] : day.inouts,
                        shifts: day.shifts.map((shift) => ({
                            ...shift,
                            time_slots:
                                isEmployee && !hasViewShiftsPermission ? [] : shift.time_slots,
                        })),
                    })),
                }));

                const employeeDayDetails = modifiedCalendarResponse.find((employee) => employee.id === employeeId);
                const dayDetails = employeeDayDetails?.days.find((day) => day.date === currentDay);

                setEmployeeDayDetails(dayDetails);
                return dayDetails;
            } catch (error) {
                if (
                    error?.name === 'CanceledError' ||
                    error?.name === 'AbortError' ||
                    error?.code === 'ERR_CANCELED'
                ) {
                    return;
                }

                console.error('Errore caricamento calendario:', error);

                if (error?.response?.status === 403) {
                    setIsForbidden(true);
                    setIsError(false);
                } else {
                    setIsError(true);
                    setIsForbidden(false);
                }

            } finally {
                if (!signal.aborted) {
                    setIsLoading(false);
                }
            }
        }
    };

    useEffect(() => {
        fetchEmployeeDayDetails();
    }, [dipendente?.id, currentDay, token, profile.employee?.permissions]);

    useEffect(() => {
        setIsLoadingJR(true);
        setIsErrorJR(false);

        const fetchData = async () => {
            try {
                const giustificativi = await getJustificationsCompanyList(token);
                setGiustificativi(giustificativi);
                setIsLoadingJR(false);
            } catch (error) {
                setIsLoadingJR(false);
                console.error('Error fetching justifications:', error);
                setIsErrorJR(true);
            }
        };

        fetchData();
    }, [token]);

    useEffect(() => {

    }, [giustificativoSelected]);

    useEffect(() => {
        async function fetchData() {
            try {
                setIsLoadingOU(true);
                const unitsData = await listOrganizationalUnits(token, true);
                setUnits(unitsData.data);
                setIsLoadingOU(false);
            } catch (error) {
                console.error('Error fetching units:', error);
                setError(true);
                setIsLoadingOU(false);
            }
        }
        fetchData();
    }, [token]);

    const handleSelectUnit = (selected) => {
        setTempSelectedUnit(selected);
    };

    const handleWorkTimeBlur = (field) => {
        let formattedTime = field === 'start' ? startTime : endTime;
        formattedTime = formattedTime.replace(/\D/g, '');

        while (formattedTime.length < 4) {
            formattedTime += '0';
        }

        formattedTime = formattedTime.slice(0, 4);
        const hours = formattedTime.slice(0, 2);
        const minutes = formattedTime.slice(2);
        formattedTime = `${hours}:${minutes}`;

        const isValidTime = /^([01]?[0-9]|2[0-4]):[0-5][0-9]$/.test(formattedTime);

        if (!isValidTime) {
            if (field === 'start') {
                setStartTime('');
            } else {
                setEndTime('');
            }
            return;
        }

        if (field === 'start') {
            setStartTime(formattedTime);
        } else {
            setEndTime(formattedTime);
        }
    };

    const handleStartTimeChange = (e) => {
        let inputValue = e.target.value;
        inputValue = inputValue.replace(/\D/g, '');
        inputValue = inputValue.slice(0, 4);
        let formattedTime = inputValue.replace(/(\d{2})(\d{0,2})/, "$1:$2");

        if (inputValue.length <= 2 && e.target.value.length < formattedTime.length) {
            formattedTime = formattedTime.replace(":", "");
        }

        setStartTime(formattedTime);

        if (endTime && formattedTime >= endTime) {
            setTimeError("L'orario di inizio deve essere precedente all'orario di fine.");
        } else {
            setTimeError('');
        }
    };

    const handleEndTimeChange = (e) => {
        let inputValue = e.target.value;
        inputValue = inputValue.replace(/\D/g, '');
        inputValue = inputValue.slice(0, 4);
        let formattedTime = inputValue.replace(/(\d{2})(\d{0,2})/, "$1:$2");

        if (inputValue.length <= 2 && e.target.value.length < formattedTime.length) {
            formattedTime = formattedTime.replace(":", "");
        }

        setEndTime(formattedTime);

        if (startTime && formattedTime <= startTime) {
            setTimeError("L'orario di fine deve essere successivo all'orario di inizio.");
        } else {
            setTimeError('');
        }
    };

    
    const convertToISOFormat = (dateString) => {
        const [day, month, year] = dateString.split('/');
        return `${year}-${month}-${day}`;
    };
    
    const handleGiustificativoChange = (giustificativo) => {
        setGiustificativoSelected(giustificativo);
    
        if (!giustificativo?.has_hours) {
            setStartTime('00:00');
            setEndTime('24:00');
        } else {
            setStartTime('');
            setEndTime('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
    
        if (!startTime || !endTime || giustificativoSelected === null) {
            setTimeError("Compila tutti i campi obbligatori.");
            return;
        }

        if (!startDay || !endDay || !startTime || !endTime) {
            setTimeError("Data e orari devono essere specificati.");
            return;
        }

        let datetimeTo = convertToISOFormat(endDay);
        if (endTime === '24:00') {
            datetimeTo = moment(datetimeTo).add(1, 'days').format('YYYY-MM-DD')+'T00:00:00';
        } else {
            datetimeTo = `${datetimeTo}T${endTime}:00`;
        }
        
        const datetimeFrom = convertToISOFormat(startDay) + `T${startTime}:00`;
    
        const data = {
            datetime_from: datetimeFrom,
            datetime_to: datetimeTo,
            note,
            justification: giustificativoSelected ? giustificativoSelected.id : null,
            ou: tempSelectedUnit ? tempSelectedUnit.id : null,
        };

        const loader = Swal.fire({
            title: 'Salvataggio in corso...',
            html: 'Attendere',
            didOpen: () => {
                Swal.showLoading();
            },
            showConfirmButton: false,
            allowOutsideClick: false,
            allowEscapeKey: false,
        });
        
        try {
            await createEmployeeJustificationRequest(token, dipendente.id, data);

            const updatedDayDetails = await fetchEmployeeDayDetails();
            const hasJustifications = !!(updatedDayDetails?.justification_requests?.length > 0);
            onAfterSave?.(currentDay, hasJustifications);
            
            setGiustificativoSelected(null);
            setStartTime('');
            setEndTime('');
            setNote('');
            setTimeError('');

            await Swal.fire({
                icon: 'success',
                title: 'Salvato!',
                text: 'La richiesta di giustificativo è stata inserita',
                timer: 1000,
                showConfirmButton: false,
            });

            loader.close();
        } catch (error) {
            console.error('Failed to update justification:', error);

            const errorMessage = estraiMessaggiErrore(error);

            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: errorMessage,
                confirmButtonText: 'OK',
            });

        } finally {
            loader.close();
        }
    };

    const handleDeleteRequest = async (justificationId) => {
        const confirmDelete = await Swal.fire({
            icon: 'warning',
            title: 'Conferma eliminazione',
            text: 'Stai eliminando un giustificativo. L\'azione non è reversibile. Confermi di voler eliminare?',
            showCancelButton: true,
            confirmButtonText: 'Conferma',
            cancelButtonText: 'Annulla',
            cancelButtonColor: '#d1d5db',
        });

        if (!confirmDelete.isConfirmed) return;

        setIsLoading(true);
        setIsError(false);

        try {
            await deleteJustificationRequest(token, justificationId);

            const updatedDayDetails = await fetchEmployeeDayDetails();
            const hasJustifications = !!(updatedDayDetails?.justification_requests?.length > 0);
            onAfterSave?.(currentDay, hasJustifications);

            Swal.fire({
                icon: 'success',
                title: 'Eliminato!',
                text: 'La richiesta è stata eliminata con successo.',
                showConfirmButton: false,
                timer: 1000,
            });
        } catch (error) {
            console.error('Errore durante eliminazione:', error);
            setIsError(true);
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: 'Errore durante l\'eliminazione del giustificativo. Riprova.',
            });
        }
    };

    const formatTimeRange = (from, to) => {
        if (!from || !to) return '';

        const fromDate = moment(from);
        const toDate = moment(to);

        let toTimeFormatted = toDate.format('HH:mm');

        const isNextDayMidnight =
            toDate.format('HH:mm') === '00:00' &&
            toDate.isSame(fromDate.clone().add(1, 'day'), 'day');

        if (isNextDayMidnight) {
            toTimeFormatted = '24:00';
        }

        return `${fromDate.format('HH:mm')} - ${toTimeFormatted}`;
    };
    

    const hasShifts = Boolean(employeeDayDetails?.shifts?.some((shift) => shift?.time_slots?.length > 0));

    const justificationOptions = hasShifts
        ? giustificativi
        : giustificativi.filter(
            (giustificativo) =>
                giustificativo?.justification?.strategy === 'increase' ||
                giustificativo?.justification?.strategy === null
        );

    const employeeLabel =
        dipendente?.last_name && dipendente?.first_name
            ? `${dipendente.last_name} ${dipendente.first_name}`
            : dipendente?.cf || '';

    return (
        <Transition.Root show={true} as={Fragment}>
            <Dialog as="div" className="relative z-[999]" onClose={() => {}}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-start justify-center px-4 pb-8 pt-10 sm:px-6">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative flex w-full max-w-5xl flex-col overflow-visible rounded-xl border border-gray-200 bg-gray-50 text-left shadow-2xl">
                                <ModalDayHeader
                                    title="Aggiungi giustificativo"
                                    subtitle={
                                        currentDay
                                            ? `${employeeLabel} · ${formatItalianDate(currentDay)}`
                                            : employeeLabel
                                    }
                                    Icon={DocumentPlusIcon}
                                    mode="justification"
                                    showWorkingAreaMode={canManageWorkingAreas || canViewWorkingAreas}
                                    showJustificationMode={canManageJustifications}
                                    onModeChange={(mode) => {
                                        if (mode === "working-area") {
                                            onSwitchToWorkingArea?.(moment(currentDay).format("YYYY-MM-DD"));
                                        }
                                    }}
                                    onClose={closeModalAddGiustificativi}
                                    onPrevious={handlePreviousDay}
                                    onNext={handleNextDay}
                                    previousDisabled={!canGoPrevious || isLoading}
                                    nextDisabled={!canGoNext || isLoading}
                                    isSenzaCalendario={isSenzaCalendario}
                                />

                                <div className="max-h-[calc(90vh-132px)] overflow-y-auto">
                                    <div className="space-y-4 px-5 py-5 sm:px-6">
                                        {(isError || isErrorJR || isForbidden) && (
                                            <div
                                                className={`rounded-lg border px-4 py-3 text-sm ${
                                                    isForbidden
                                                        ? 'border-orange-200 bg-orange-50 text-orange-700'
                                                        : 'border-red-200 bg-red-50 text-red-700'
                                                }`}
                                            >
                                                {isForbidden
                                                    ? 'Non disponi dei permessi necessari per visualizzare tutti i dati della giornata.'
                                                    : 'Si è verificato un errore durante il caricamento dei dati. Riprova.'}
                                            </div>
                                        )}

                                        {/* RIEPILOGO GIORNATA */}
                                        <section>
                                            {isLoading ? (
                                                <div className="flex min-h-48 items-center justify-center rounded-lg border border-gray-200 bg-gray-50">
                                                    <div className="flex items-center gap-3 text-sm font-semibold text-gray-500">
                                                        <CircularProgress size={24} />
                                                        Caricamento giornata...
                                                    </div>
                                                </div>
                                            ) : !employeeDayDetails ? (
                                                <div className="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-5 py-10 text-center">
                                                    <BriefcaseIcon className="mx-auto h-8 w-8 text-gray-400" />
                                                    <p className="mt-3 text-sm font-semibold text-gray-700">
                                                        Nessun dato disponibile
                                                    </p>
                                                    <p className="mt-1 text-xs text-gray-500">
                                                        Non sono presenti informazioni per il giorno selezionato
                                                    </p>
                                                </div>
                                            ) : (
                                                <div className="space-y-4">
                                                    {/* TURNI */}
                                                    <div className="overflow-hidden rounded-lg border border-gray-200">
                                                        <div className="flex items-center justify-between border-b border-gray-200 bg-indigo-100/60 px-4 py-2.5">
                                                            <div className="flex items-center gap-2">
                                                                <BriefcaseIcon className="h-4 w-4 shrink-0 text-indigo-600" />

                                                                <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-700">
                                                                    Turni
                                                                </span>
                                                            </div>
                                                        </div>

                                                        {hasShifts && (
                                                            <div className="hidden grid-cols-[20%_25%_25%_30%] border-b border-gray-200 bg-white px-3 py-2 md:grid">
                                                                <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                                                    Stato
                                                                </div>
                                                                <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                                                    Fascia oraria
                                                                </div>
                                                                <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 px-2">
                                                                    Pause
                                                                </div>
                                                                <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                                                    Area Lavoro
                                                                </div>
                                                            </div>
                                                        )}

                                                        {hasShifts ? (
                                                            <div className="divide-y divide-gray-200">
                                                                {employeeDayDetails.shifts.flatMap((shift, shiftIndex) =>
                                                                    (shift.time_slots || []).map((slot, slotIndex) => {
                                                                        const isPending = shift.status === false;

                                                                        return (
                                                                            <div
                                                                                key={`shift-${shiftIndex}-slot-${slot.id ?? slotIndex}`}
                                                                                className={`grid grid-cols-1 gap-3 px-3 py-1.5 md:grid-cols-[20%_25%_25%_30%] md:items-center md:gap-0 ${
                                                                                    isPending ? 'bg-orange-50/60' : 'bg-white'
                                                                                }`}
                                                                            >
                                                                                <div className="min-w-0 md:pr-3">
                                                                                    <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400 md:hidden">
                                                                                        Stato
                                                                                    </p>

                                                                                    {isPending ? (
                                                                                        <span className="inline-flex items-center rounded-full bg-orange-100 px-2.5 py-1 text-[10px] font-semibold uppercase text-orange-700">
                                                                                            Da confermare
                                                                                        </span>
                                                                                    ) : (
                                                                                        <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-1 text-[10px] font-semibold uppercase text-green-700">
                                                                                            Confermato
                                                                                        </span>
                                                                                    )}
                                                                                </div>

                                                                                <div className="min-w-0 md:pr-3">
                                                                                    <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400 md:hidden">
                                                                                        Fascia oraria
                                                                                    </p>
                                                                                    <span className="text-sm font-semibold text-gray-800">
                                                                                        {slot.start_at || '--:--'} - {slot.end_at || '--:--'}
                                                                                    </span>
                                                                                </div>

                                                                                <div className="min-w-0">
                                                                                    <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400 md:hidden">
                                                                                        Pause
                                                                                    </p>

                                                                                    {slot.breaks?.length > 0 ? (
                                                                                        <div className="flex flex-wrap">
                                                                                            {slot.breaks.map((br, brIndex) => {
                                                                                                const isHidden = !(br?.is_visible ?? true);
                                                                                                const isLast = brIndex === slot.breaks.length - 1;

                                                                                                return (
                                                                                                    <React.Fragment key={br.id ?? brIndex}>
                                                                                                        <span
                                                                                                            className={`inline-flex items-center py-0 px-2 text-[12px] font-medium ${
                                                                                                                isHidden
                                                                                                                    ? 'text-gray-400'
                                                                                                                    : 'text-gray-600'
                                                                                                            }`}
                                                                                                        >
                                                                                                            {isHidden && (
                                                                                                                <EyeSlashIcon className="h-3.5 w-3.5 shrink-0 text-red-500" />
                                                                                                            )}

                                                                                                            {br.start_at} - {br.end_at}
                                                                                                        </span>

                                                                                                        {!isLast && (
                                                                                                            <span
                                                                                                                className="inline-flex items-center text-xs text-gray-700"
                                                                                                                aria-hidden="true"
                                                                                                            >
                                                                                                                |
                                                                                                            </span>
                                                                                                        )}
                                                                                                    </React.Fragment>
                                                                                                );
                                                                                            })}
                                                                                        </div>
                                                                                    ) : (
                                                                                        <span className="text-xs text-gray-400 px-2">
                                                                                            Nessuna pausa
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                                                                                                                                                                                                                                 <div className="min-w-0 md:pr-3">
                                                                                    <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400 md:hidden">
                                                                                        Area lavoro
                                                                                    </p>

                                                                                    {slot.working_area_id ? (
                                                                                        <div className="flex min-w-0 items-center gap-2">
                                                                                            <MapPinIcon className="h-4 w-4 shrink-0 text-indigo-600" />

                                                                                            <CustomTooltipComponent title={slot.working_area_name || ""} placement="top" arrow>
                                                                                                <span className="block truncate text-[13px] font-semibold text-indigo-700">
                                                                                                    {slot.working_area_name || "Area assegnata"}
                                                                                                </span>
                                                                                            </CustomTooltipComponent>
                                                                                        </div>
                                                                                    ) : (
                                                                                        <span className="text-xs text-gray-400">
                                                                                            Nessuna area
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <div className="px-4 py-3 text-center bg-gray-50/20">
                                                                <BriefcaseIcon className="mx-auto h-5 w-5 text-gray-300 inline-block" />
                                                                <p className="mt-1 text-xs font-medium text-gray-500">
                                                                    Nessun turno presente
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* GIUSTIFICATIVI PRESENTI */}
                                                    <div className="overflow-hidden rounded-lg border border-gray-200">
                                                        <div className="flex items-center justify-between border-b border-gray-200 bg-indigo-100/60 px-4 py-2.5">
                                                            <div className="flex items-center gap-2">
                                                                <ClipboardDocumentCheckIcon className="h-4 w-4 shrink-0 text-indigo-600" />
                                                                <span className="text-[11px] font-semibold uppercase tracking-wide text-indigo-800">
                                                                    Giustificativi
                                                                </span>
                                                            </div>

                                                            <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-indigo-700 ring-1 ring-inset ring-indigo-100">
                                                                {employeeDayDetails?.justification_requests?.length || 0}
                                                            </span>
                                                        </div>

                                                        {employeeDayDetails?.justification_requests?.length > 0 ? (
                                                            <>
                                                                <div className="hidden grid-cols-[20%_25%_1fr_8%] border-b border-gray-200 bg-white px-3 py-2 md:grid">
                                                                    <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                                                        Stato
                                                                    </div>
                                                                    <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                                                        Fascia oraria
                                                                    </div>
                                                                    <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                                                        Tipologia
                                                                    </div>
                                                                    <div className="text-center text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                                                        Azioni
                                                                    </div>
                                                                </div>

                                                                <div className="divide-y divide-gray-200">
                                                                    {employeeDayDetails.justification_requests.map((j, jIndex) => (
                                                                        <div
                                                                            key={j.id ?? `just-${jIndex}`}
                                                                            className={`grid grid-cols-1 gap-3 px-3 py-1.5 md:grid-cols-[20%_25%_1fr_8%] md:items-center md:gap-0 ${
                                                                                j.status === 'approved'
                                                                                    ? 'bg-white'
                                                                                    : 'bg-orange-50/60'
                                                                            }`}
                                                                        >
                                                                            <div className="md:pr-3">
                                                                                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400 md:hidden">
                                                                                    Stato
                                                                                </p>

                                                                                {j.status === 'approved' ? (
                                                                                    <span className="inline-flex rounded-full bg-green-100 px-2.5 py-1 text-[10px] font-semibold uppercase text-green-700">
                                                                                        Approvato
                                                                                    </span>
                                                                                ) : (
                                                                                    <span className="inline-flex rounded-full bg-orange-100 px-2.5 py-1 text-[10px] font-semibold uppercase text-orange-700">
                                                                                        In attesa
                                                                                    </span>
                                                                                )}
                                                                            </div>

                                                                            <div className="md:pr-3">
                                                                                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400 md:hidden">
                                                                                    Fascia oraria
                                                                                </p>
                                                                                <span className="text-sm font-semibold text-gray-700">
                                                                                    {formatTimeRange(j.datetime_from, j.datetime_to)}
                                                                                </span>
                                                                            </div>

                                                                            <div className="min-w-0 md:pr-3">
                                                                                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400 md:hidden">
                                                                                    Tipologia
                                                                                </p>

                                                                                <div className="flex min-w-0 items-center gap-2">
                                                                                    <CustomTooltipComponent
                                                                                        title={j.description || ''}
                                                                                        placement="top"
                                                                                        arrow
                                                                                    >
                                                                                        <span className="inline-flex min-w-0 items-center gap-1 text-[13px] font-semibold text-gray-600">
                                                                                            <span className="truncate">
                                                                                                {j.description || 'Giustificativo'}
                                                                                            </span>

                                                                                            {j?.justification?.justification?.is_marker_area && (
                                                                                                <MapPinIcon className="h-4 w-4 shrink-0 text-indigo-600" />
                                                                                            )}
                                                                                        </span>
                                                                                    </CustomTooltipComponent>
                                                                                </div>

                                                                                {j.note && (
                                                                                    <p className="mt-1 text-[12px] text-gray-400">
                                                                                        Nota: {j.note}
                                                                                    </p>
                                                                                )}
                                                                            </div>

                                                                            <div className="flex items-center justify-start md:justify-center">
                                                                                <p className="mr-3 text-[10px] font-semibold uppercase tracking-wide text-gray-400 md:hidden">
                                                                                    Azioni
                                                                                </p>

                                                                                <CustomTooltipComponent
                                                                                    title="Elimina giustificativo"
                                                                                    arrow
                                                                                >
                                                                                    <span className="inline-flex">
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() => handleDeleteRequest(j.id)}
                                                                                            aria-label={`Elimina giustificativo ${j.description || ''}`}
                                                                                            className="flex h-6 w-8 shrink-0 items-center justify-center rounded-md bg-red-50 text-red-500 transition-colors hover:bg-red-100 hover:text-red-700"
                                                                                        >
                                                                                            <TrashIcon className="h-4 w-4 shrink-0" />
                                                                                        </button>
                                                                                    </span>
                                                                                </CustomTooltipComponent>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <div className="px-4 py-4 text-center bg-gray-50/20">
                                                                <p className="text-xs font-medium text-gray-500">
                                                                    Nessun giustificativo presente
                                                                </p>
                                                                <p className="mt-1 text-xs text-gray-400">
                                                                    Puoi inserirne uno utilizzando il modulo sottostante
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </section>
                                        {employeeDayDetails ? (
                                            <>
                                                <div className="border-t border-gray-200" />

                                                {/* NUOVO GIUSTIFICATIVO */}
                                                <section>
                                                    <div className="mb-3">
                                                        <h3 className="text-xs font-semibold uppercase tracking-wide text-indigo-800">
                                                            Nuovo giustificativo
                                                        </h3>
                                                        <p className="text-xs text-gray-500">
                                                            Compila i dati della richiesta per il giorno selezionato
                                                        </p>
                                                    </div>

                                                    <form id="add-justification-form" onSubmit={handleSubmit} className="overflow-visible rounded-lg border border-gray-200 bg-white">
                                                        <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
                                                            {/* DIPENDENTE */}
                                                            <div>
                                                                <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                                                    Dipendente
                                                                </label>

                                                                <div className="flex h-9 w-full items-center rounded-md border border-gray-300 bg-gray-100/60 px-3 text-sm text-gray-600 shadow-sm cursor-not-allowed">
                                                                    {employeeLabel || "—"}
                                                                </div>
                                                            </div>

                                                            {/* GIORNO */}
                                                            <div>
                                                                <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                                                    Giorno selezionato
                                                                </label>

                                                                <div className="flex h-9 w-full items-center rounded-md border border-gray-300 bg-gray-100/60 px-3 text-sm text-gray-600 shadow-sm cursor-not-allowed">
                                                                    {currentDay ? moment(currentDay).format("DD/MM/YYYY") : "—"}
                                                                </div>
                                                            </div>

                                                            {/* GIUSTIFICATIVO */}
                                                            <div className="sm:col-span-2">
                                                                <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                                                    Giustificativo *
                                                                </label>

                                                                {isLoadingJR ? (
                                                                    <div className="flex min-h-9 items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 text-xs text-gray-500">
                                                                        <CircularProgress size={16} />
                                                                        Caricamento giustificativi...
                                                                    </div>
                                                                ) : (
                                                                    <div className="rounded-md border border-gray-200 bg-white max-h-28">
                                                                        <ComboboxAutocomplete
                                                                            options={justificationOptions.map((giustificativo) => ({
                                                                                id: giustificativo.id,
                                                                                name: giustificativo.justification.description,
                                                                                value: giustificativo.id,
                                                                                has_hours: giustificativo?.justification?.has_hours,
                                                                            }))}
                                                                            bgActiveColor="bg-indigo-100"
                                                                            textActiveColor="text-gray-900"
                                                                            placeholder="Seleziona il giustificativo..."
                                                                            onChange={(selectedOption) => {
                                                                                setGiustificativoSelected(selectedOption ?? null);
                                                                                handleGiustificativoChange(selectedOption);
                                                                            }}
                                                                            value={giustificativoSelected}
                                                                            displayValue={(selected) => {const selectedOption = giustificativi.find((giustificativo) => Number(giustificativo.id) === Number(selected?.id));
                                                                                return selectedOption ? selectedOption.justification.description : '';}}
                                                                            renderOption={(option) => (
                                                                                <div className="flex min-w-0 items-center justify-between gap-3">
                                                                                    <span className="truncate text-sm text-gray-700">
                                                                                        {option.name}
                                                                                    </span>

                                                                                    {option.code && (
                                                                                        <span className="shrink-0 rounded bg-indigo-100 px-2 py-0.5 text-[10px] font-bold uppercase text-indigo-700">
                                                                                            {option.code}
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                            positionOptions="absolute"
                                                                            zIndex="z-[1000]"
                                                                            required
                                                                            borderClass="border-gray-200"
                                                                            optionsClassName='max-h-52 border border-gray-300 shadow-md'
                                                                            openDirection='top'
                                                                        />
                                                                    </div>
                                                                )}

                                                                {!hasShifts && (
                                                                    <p className="mt-1 text-[11px] text-gray-500">
                                                                        Non essendo presenti turni nella giornata, vengono mostrati solo i giustificativi compatibili
                                                                    </p>
                                                                )}
                                                            </div>

                                                            {/* ORARI */}
                                                            {giustificativoSelected?.has_hours && (
                                                                <>
                                                                    <div>
                                                                        <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                                                            Dalle ore *
                                                                        </label>
                                                                        <div className="relative">
                                                                            <input
                                                                                type="text"
                                                                                placeholder="hh:mm"
                                                                                value={startTime}
                                                                                onInput={handleStartTimeChange}
                                                                                onBlur={() => handleWorkTimeBlur('start')}
                                                                                className="block h-9 w-full rounded-md border border-gray-300 bg-white px-3 pr-9 text-sm text-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                                                                required
                                                                            />
                                                                            <ClockIcon className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                                                        </div>
                                                                    </div>

                                                                    <div>
                                                                        <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                                                            Alle ore *
                                                                        </label>
                                                                        <div className="relative">
                                                                            <input
                                                                                type="text"
                                                                                placeholder="hh:mm"
                                                                                value={endTime}
                                                                                onInput={handleEndTimeChange}
                                                                                onBlur={() => handleWorkTimeBlur('end')}
                                                                                className="block h-9 w-full rounded-md border border-gray-300 bg-white px-3 pr-9 text-sm text-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                                                                                required
                                                                            />
                                                                            <ClockIcon className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                                                        </div>
                                                                    </div>
                                                                </>
                                                            )}

                                                            {/* UNITÀ */}
                                                            <div className="sm:col-span-2">
                                                                <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                                                    Associa unità
                                                                </label>

                                                                {isLoadingOu ? (
                                                                    <div className="flex min-h-9 items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 text-xs text-gray-500">
                                                                        <CircularProgress size={16} />
                                                                        Caricamento unità...
                                                                    </div>
                                                                ) : (
                                                                    <div className="rounded-md border border-gray-200 bg-white">
                                                                        <ComboboxAutocomplete
                                                                            options={units}
                                                                            bgActiveColor="bg-indigo-100"
                                                                            textActiveColor="text-gray-900"
                                                                            placeholder="Seleziona l'Unità Organizzativa"
                                                                            onChange={handleSelectUnit}
                                                                            value={tempSelectedUnit}
                                                                            positionOptions="absolute"
                                                                            zIndex="z-[1200]"
                                                                            borderClass="border-gray-200"
                                                                            openDirection='top'
                                                                            optionsClassName='max-h-52 border border-gray-300 shadow-md'
                                                                        />
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* NOTE */}
                                                            <div className="sm:col-span-2">
                                                                <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                                                    Note
                                                                </label>
                                                                <textarea
                                                                    rows={3}
                                                                    className="block w-full resize-y rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 placeholder:text-gray-400"
                                                                    value={note}
                                                                    onChange={(e) => setNote(e.target.value)}
                                                                    placeholder="Aggiungi una nota facoltativa..."
                                                                />
                                                            </div>
                                                        </div>

                                                        {timeError && (
                                                            <div className="border-t border-red-100 bg-red-50 px-4 py-2.5 text-xs font-medium text-red-600">
                                                                {timeError}
                                                            </div>
                                                        )}
                                                    </form>
                                                </section>
                                            </>
                                        ) : ('')}
                                    </div>
                                </div>

                                {/* FOOTER */}
                                <div className="flex flex-col-reverse gap-3 border-t rounded-b-lg border-gray-200 bg-gray-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                                    {employeeDayDetails ? (
                                        <p className="text-xs text-gray-500">
                                            I campi contrassegnati con * sono obbligatori
                                        </p>
                                    ) : (<div></div>)}
                                    <div className="flex items-center justify-end gap-3">
                                        {employeeDayDetails && (
                                            <button
                                                type="submit"
                                                form="add-justification-form"
                                                ref={cancelButtonRef}
                                                disabled={isLoading || isLoadingJR || !giustificativoSelected}
                                                className="inline-flex min-w-28 items-center justify-center rounded-md bg-indigo-600 px-5 py-2 text-xs font-semibold uppercase text-white shadow-sm transition-colors hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                Salva
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={closeModalAddGiustificativi}
                                            className="rounded-md border border-gray-300 bg-white px-5 py-2 text-xs font-semibold uppercase text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
                                        >
                                            Chiudi
                                        </button>

                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
};

export default AddGiustificativoModalAssignWH;
